// EditRiskModal.jsx 🛠️ Risk Edit Form – Fully Synced with DB
import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import axios from "axios";
import "./EditRiskModal.css";

const EditRiskModal = ({ show, onClose, data, onSuccess, showToast }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resourcesOptions, setResourcesOptions] = useState([]);
  const [threatsOptions, setThreatsOptions] = useState([]);

  const fetchOptions = async () => {
    try {
      const [resResources, resThreats] = await Promise.all([
        axios.get("/api/options_ressources.php"),
        axios.get("/api/options_menaces.php"),
      ]);
      setResourcesOptions(resResources.data?.ressources || []);
      setThreatsOptions(resThreats.data?.menaces || []);
    } catch {
      showToast("Error loading options", "danger");
    }
  };

  const fetchRiskData = async () => {
    if (!data?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/edit_risk.php?id=${data.id}`);
      const r = res.data?.risk;
      if (!r) throw new Error("Risk not found");

      // Initialize default consequence keys
      const cons = {
        Strategic: 0,
        Financial: 0,
        Reputational: 0,
        Operational: 0,
        Legal: 0,
      };

      // Convert keys from backend
      Object.entries(r.consequences || {}).forEach(([key, val]) => {
        const cleanKey = key.trim();
        if (cons.hasOwnProperty(cleanKey)) {
          cons[cleanKey] = parseInt(val);
        }
      });

      setForm({
        id: r.id,
        name: r.name || "",
        criterion: r.criterion || "D",
        responsible: r.responsible || "",
        business_unit: r.business_unit || "",
        process: r.process || "",
        ressources: r.ressources || [],
        menaces: r.menaces || [],
        consequences: cons,
      });
    } catch (err) {
      console.error("Risk fetch error:", err);
      showToast("Could not load risk data", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && data?.id) {
      fetchOptions();
      fetchRiskData();
    }
  }, [show, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e) => {
    const { name, selectedOptions } = e.target;
    const values = Array.from(selectedOptions).map((o) => o.value);
    setForm((prev) => ({ ...prev, [name]: values }));
  };

  const handleConsequenceChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      consequences: {
        ...prev.consequences,
        [name]: parseInt(value),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/api/edit_risk.php", form);
      showToast("Risk successfully updated!");
      onSuccess?.("Risk updated");
      onClose();
    } catch (err) {
      console.error(err);
      showToast("Error during update", "danger");
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="edit-risk-modal"
    >
      <Form onSubmit={handleSubmit}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2 text-warning" />
              Edit Risk
            </h5>
            <button className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {loading || !form ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
                <Spinner animation="border" variant="light" />
              </div>
            ) : (
              <>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label>Criterion</Form.Label>
                    <Form.Select name="criterion" value={form.criterion} onChange={handleChange}>
                      <option value="D">Availability</option>
                      <option value="I">Integrity</option>
                      <option value="C">Confidentiality</option>
                      <option value="T">Traceability</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label>Responsible</Form.Label>
                    <Form.Control name="responsible" value={form.responsible} onChange={handleChange} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label>Business Unit</Form.Label>
                    <Form.Control name="business_unit" value={form.business_unit} onChange={handleChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label>Process</Form.Label>
                    <Form.Control name="process" value={form.process} onChange={handleChange} />
                  </div>
                </div>

                <div className="mb-3">
                  <Form.Label>Risk Name</Form.Label>
                  <Form.Control name="name" value={form.name} onChange={handleChange} />
                </div>

                {/* Resources & Threats */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label>Resources</Form.Label>
                    <Form.Select
                      name="ressources"
                      multiple
                      value={form.ressources}
                      onChange={handleSelectChange}
                    >
                      {resourcesOptions.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name} ({r.type})
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label>Threats</Form.Label>
                    <Form.Select
                      name="menaces"
                      multiple
                      value={form.menaces}
                      onChange={handleSelectChange}
                    >
                      {threatsOptions.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>

                {/* Consequences */}
                <div className="mb-3">
                  <Form.Label>Consequences</Form.Label>
                  <div className="row">
                    {Object.entries(form.consequences).map(([axe, value]) => (
                      <div key={axe} className="col-md-4 mb-2">
                        <Form.Label>{axe}</Form.Label>
                        <Form.Control
                          type="number"
                          name={axe}
                          value={value}
                          onChange={handleConsequenceChange}
                          min="0"
                          max="4"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="warning" type="submit" disabled={loading || !form}>
              Update
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default EditRiskModal;
